import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { useI18n } from "@wato/i18n/react";
import type { MessageKey } from "@wato/i18n";
import { Button, Collapsible } from "@wato/ui";
import { LanguageToggle } from "../../components/common";
import { ProductTourHost } from "../../components/ProductTour";
import { useAuth } from "../../auth/AuthProvider";
import { getThemePreference, setThemePreference, type ThemePreference } from "../../lib/theme";
import { Avatar } from "../../components/Avatar";
import { UsageMeters } from "../billing/UsageMeters";
import { DeviceNotifications } from "../mobile-pwa/DeviceNotifications";
import { InstallAppMenuItem } from "../mobile-shell/InstallAppMenuItem";
import { WalletChip } from "../billing/WalletChip";
import { popoverPlacementClass, type PopoverPlacement } from "./popover-placement";
import { AvailabilityToggle } from "./AvailabilityToggle";
import { HandoverMenu } from "./HandoverMenu";

/** Q4-XCT-03: opening one header popover announces it so every other one closes. */
export const HEADER_POPOVER_EVENT = "wato:header-popover-open";

/** Esc / outside-click closing for the small header popovers; only one is open at a time. */
function usePopover() {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const id = useId();
  useEffect(() => {
    if (!open) return;
    window.dispatchEvent(new CustomEvent(HEADER_POPOVER_EVENT, { detail: id }));
    const onOther = (e: Event) => {
      if ((e as CustomEvent<string>).detail !== id) setOpen(false);
    };
    window.addEventListener(HEADER_POPOVER_EVENT, onOther);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        button.current?.focus();
      }
    };
    const onDown = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      window.removeEventListener(HEADER_POPOVER_EVENT, onOther);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open]);
  return { open, setOpen, root, button, id };
}

const ABOUT: readonly { title: MessageKey; text: MessageKey }[] = [
  { title: "dashboard.nav.helpAboutSearch", text: "dashboard.nav.shellHintSearch" },
  { title: "dashboard.nav.helpAboutAutomation", text: "dashboard.nav.shellHintAutomation" },
  { title: "dashboard.nav.helpAboutUsage", text: "dashboard.nav.shellHintPlan" },
  { title: "dashboard.nav.helpAboutWallet", text: "dashboard.nav.shellHintWallet" },
];

/**
 * FB3-20 / OWN-320: the single help block — product tours, the Help Center and every former «what is this?»
 * explainer — now a section of the account menu. It stays mounted while the menu is closed so
 * `ProductTourHost` can still auto-start tours.
 */
function HelpSection({ workspaceId, onPick }: { workspaceId: string; onPick: () => void }) {
  const { t } = useI18n();
  return (
    <Collapsible
      title={t("dashboard.nav.helpMenu")}
      className="border-t border-line px-3"
      testId="help-menu"
    >
      <Link
        to={`/w/${workspaceId}/help-center`}
        onClick={onPick}
        className="block rounded-md px-1 py-2 text-sm font-medium text-fg hover:bg-surface-sunken focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus"
      >
        {t("dashboard.nav.helpCenter")}
      </Link>
      <p className="pt-2 text-xs font-semibold text-fg-muted">{t("setup.tour.menu")}</p>
      <ProductTourHost workspaceId={workspaceId} embedded onPick={onPick} />
      <Collapsible title={t("dashboard.nav.helpAboutHeading")} testId="help-about">
        <dl className="m-0 space-y-2 pb-2 pt-1 text-sm">
          {ABOUT.map((item) => (
            <div key={item.title}>
              <dt className="font-medium text-fg">{t(item.title)}</dt>
              <dd className="m-0 text-fg-muted">{t(item.text)}</dd>
            </div>
          ))}
        </dl>
      </Collapsible>
    </Collapsible>
  );
}

function MenuLink({
  to,
  onClick,
  children,
}: {
  to: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="block rounded-md px-3 py-2 text-sm text-fg hover:bg-surface-sunken focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus"
    >
      {children}
    </Link>
  );
}

/** Fallback label when no display name is set: the email local-part, never the full address or an id. */
export function emailLocalPart(email: string): string {
  return email.split("@")[0]?.trim() ?? "";
}

/** FB3-20: avatar menu — settings, usage + wallet (moved out of the header), language on phones, sign out. */
export function AvatarMenu({
  workspaceId,
  placement = "end-down",
  showName = true,
}: {
  workspaceId: string;
  placement?: PopoverPlacement;
  /** The display name next to the avatar (desktop header style); off in the icon rail. */
  showName?: boolean;
}) {
  const { t } = useI18n();
  const { state, signOut } = useAuth();
  const navigate = useNavigate();
  const { open, setOpen, root, button, id } = usePopover();
  const email = state.status === "ready" ? state.me.user.email : "";
  const displayName = state.status === "ready" ? (state.me.user.displayName ?? "").trim() : "";
  const who = displayName || emailLocalPart(email);
  const initial = ([...who][0] ?? "?").toUpperCase();
  const photo = state.status === "ready" ? (state.me.user.avatarUrl ?? null) : null;
  return (
    <div ref={root} className="relative">
      <button
        ref={button}
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={id}
        aria-label={t("dashboard.nav.avatarMenu")}
        title={who || t("dashboard.nav.avatarMenu")}
        onClick={() => setOpen(!open)}
        className="inline-flex h-8 min-w-8 items-center justify-center rounded-full bg-surface-sunken px-0 text-sm font-semibold text-fg hover:bg-line focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus md:px-1"
      >
        <Avatar src={photo} initials={initial} className="size-7 bg-transparent text-fg" />
        {displayName && showName ? (
          <span
            data-testid="avatar-name"
            dir="auto"
            className="ms-2 hidden max-w-[10rem] truncate text-sm font-medium md:inline"
          >
            {displayName}
          </span>
        ) : null}
      </button>
      <div
        id={id}
        hidden={!open}
        className={`absolute z-50 max-h-[80vh] w-72 max-w-[90vw] overflow-y-auto rounded-md border border-line bg-surface p-2 shadow-lg ${popoverPlacementClass(placement)}`}
      >
        {open ? (
          <>
            <p className="px-3 py-1 text-xs text-fg-muted">
              {t("dashboard.hello")}
              {who ? `, ${who}` : ""}
            </p>
            <MenuLink to={`/w/${workspaceId}/settings`} onClick={() => setOpen(false)}>
              {t("dashboard.nav.settingsGear")}
            </MenuLink>
            <div className="border-t border-line px-3 py-2">
              <p className="pb-2 text-xs font-semibold text-fg-muted">
                {t("dashboard.nav.usageAndWallet")}
              </p>
              <div className="flex flex-wrap gap-2">
                <UsageMeters workspaceId={workspaceId} />
                <WalletChip workspaceId={workspaceId} />
              </div>
            </div>
            <AvailabilityToggle workspaceId={workspaceId} />
            <HandoverMenu workspaceId={workspaceId} />
            <DeviceNotifications workspaceId={workspaceId} />
            <InstallAppMenuItem onInstalled={() => setOpen(false)} />
            <div className="border-t border-line px-3 py-2">
              <LanguageToggle />
            </div>
            <ThemePicker />
          </>
        ) : null}
        <HelpSection workspaceId={workspaceId} onPick={() => setOpen(false)} />
        {open ? (
          <div className="border-t border-line p-2">
            <Button
              variant="ghost"
              size="sm"
              fullWidth
              className="justify-start!"
              onClick={() => {
                void signOut().then(() => navigate("/login", { replace: true }));
              }}
            >
              {t("dashboard.shell.signOut")}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

const THEME_OPTIONS: ReadonlyArray<[ThemePreference, MessageKey]> = [
  ["light", "dashboard.shell.themeLight"],
  ["dark", "dashboard.shell.themeDark"],
  ["system", "dashboard.shell.themeSystem"],
];

function ThemePicker() {
  const { t } = useI18n();
  const [pref, setPref] = useState<ThemePreference>(getThemePreference);
  return (
    <Collapsible
      title={t("dashboard.shell.theme")}
      summary={t(THEME_OPTIONS.find(([v]) => v === pref)?.[1] ?? "dashboard.shell.themeSystem")}
      className="border-t border-line px-3"
      testId="theme-picker"
    >
      <div role="group" aria-label={t("dashboard.shell.theme")} className="flex gap-1">
        {THEME_OPTIONS.map(([value, key]) => (
          <Button
            key={value}
            size="sm"
            variant={pref === value ? "primary" : "secondary"}
            aria-pressed={pref === value}
            className="flex-1 px-2!"
            onClick={() => {
              setThemePreference(value);
              setPref(value);
            }}
          >
            {t(key)}
          </Button>
        ))}
      </div>
    </Collapsible>
  );
}
